import React, { FunctionComponent, useState } from 'react'
import axios from 'axios'
import Toaster from '../Toaster'
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'
import { BriefThumbWipe } from './BriefThumbWipe'
import styles from './AdminToolsTab.module.css'

interface Props {
  teamID: string
  customerID: string
}

/* the briefs whose stored copies can be wiped, as cards like the Briefs board's */
const BRIEFS = [
  { type: 'health-check', title: 'Health check' },
  { type: 'fleet-makeup', title: 'Fleet makeup' },
  { type: 'security', title: 'Security' },
] as const

type BriefType = typeof BRIEFS[number]['type']

export const AdminToolsTab: FunctionComponent<Props> = ({ teamID, customerID }) => {
  /* the briefs being wiped right now */
  const [wiping, setWiping] = useState<BriefType[]>([])
  const [confirmAll, setConfirmAll] = useState(false)

  const wipe = (type: BriefType) => axios.delete(`/api/teams/${teamID}/customers/${customerID}/briefs/${type}`)

  const handleWipe = (type: BriefType) => {
    setWiping(current => [...current, type])
    wipe(type)
      .then(() => {
        Toaster.notifySuccess('Brief cache deleted successfully. It will be rebuilt on next access.')
      })
      .catch(err => {
        Toaster.notifyFailure(err.message || 'An error occurred while deleting the brief cache')
      })
      .finally(() => {
        setWiping(current => current.filter(t => t !== type))
      })
  }

  /* one at a time, so the briefs' deletes don't contend with each other; any that fail are named in the toast */
  const handleWipeAll = async () => {
    setConfirmAll(false)
    setWiping(BRIEFS.map(brief => brief.type))
    const failed: string[] = []
    // eslint-disable-next-line no-restricted-syntax -- in turn, not all at once
    for (const { type, title } of BRIEFS) {
      try {
        // eslint-disable-next-line no-await-in-loop -- in turn, not all at once
        await wipe(type)
      } catch (err) {
        failed.push(`${title} (${(err as Error)?.message || 'unknown error'})`)
      }
      setWiping(current => current.filter(t => t !== type))
    }
    if (failed.length === 0) {
      Toaster.notifySuccess('All brief caches deleted. Each brief will be rebuilt on next access.')
    } else {
      Toaster.notifyFailure(`Couldn't wipe: ${failed.join(', ')}`)
    }
  }

  return (
    <div className={`surface ${styles.container}`}>
      {/* the title with its description right under it, and Wipe all beside them */}
      <header className={styles.header}>
        <div>
          <h2 className={styles.heading}>Admin Tools</h2>
          <p className={styles.description}>
            Select a brief cache to delete. This forces the brief to be regenerated from fresh data upon next access.
          </p>
        </div>
        <button
          type='button'
          className={`btn danger ${styles.wipeAll}`}
          onClick={() => setConfirmAll(true)}
          disabled={wiping.length > 0}
        >
          Wipe all brief data
        </button>
      </header>

      <ul className={styles.grid}>
        {BRIEFS.map(({ type, title }) => {
          const busy = wiping.includes(type)
          return (
            <li key={type} className={styles.card} data-testid={`admin-brief-${type}`}>
              <h3 className={styles.cardTitle}>{title}</h3>
              <BriefThumbWipe brief={type} wiping={busy} className={styles.thumb} />
              <button
                type='button'
                className={`btn secondary ${styles.wipe}`}
                onClick={() => handleWipe(type)}
                disabled={busy}
                aria-label={`Wipe ${title} brief data`}
              >
                {busy ? 'Wiping...' : 'Wipe brief data'}
              </button>
            </li>
          )
        })}
      </ul>

      {confirmAll && (
        <ConfirmationModal
          title='Wipe all brief data'
          message='This deletes the stored copy of every brief for this customer. Each one will be regenerated from fresh data the next time it is opened.'
          yesLabel='Wipe all'
          cannotUndoWarning
          onClose={() => setConfirmAll(false)}
          onYes={handleWipeAll}
        />
      )}
    </div>
  )
}
